import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  /** Tone of background — useful when used inside cream sections. */
  tone?: 'default' | 'cream';
}

/**
 * Thoughtful empty / zero-result state. Used in listings, favorites, search, etc.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  tone = 'default',
}: EmptyStateProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-16 rounded-2xl border',
        tone === 'cream'
          ? 'bg-surface-cream border-border-subtle'
          : 'bg-surface border-border-subtle',
        className,
      )}
    >
      {icon && (
        <div
          aria-hidden
          className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent [&>svg]:h-7 [&>svg]:w-7"
        >
          {icon}
        </div>
      )}
      <h3 className="font-display text-h3 text-ink leading-tight">{title}</h3>
      {description && (
        <p className="mt-2 max-w-md text-sm text-ink-secondary leading-relaxed">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
